import { useSearchParams } from 'react-router-dom';
import { obterResumoDaSincronizacao } from '../api/sincronizacao.js';
import { useConsulta } from '../api/useConsulta.js';
import { usePode } from '../autorizacao/usePode.js';
import { Abas, PainelDeAba } from '../componentes/Abas.js';
import type { Aba } from '../componentes/Abas.js';
import { Cartao } from '../componentes/Cartao.js';
import { Pagina } from '../componentes/Pagina.js';
import { AbaCategorias } from './sincronizacao/AbaCategorias.js';
import { AbaDescartados } from './sincronizacao/AbaDescartados.js';
import { AbaLotes } from './sincronizacao/AbaLotes.js';
import { AbaLotesDeProduto } from './sincronizacao/AbaLotesDeProduto.js';
import { AbaProdutos } from './sincronizacao/AbaProdutos.js';
import { AbaSaldos } from './sincronizacao/AbaSaldos.js';
import { CartaoCargaInicial } from './sincronizacao/CartaoCargaInicial.js';
import { CartaoSincronizacaoPeriodica } from './sincronizacao/CartaoSincronizacaoPeriodica.js';
import { ResumoDaSincronizacao } from './sincronizacao/ResumoDaSincronizacao.js';
import './Sincronizacao.css';

const GRUPO = 'sincronizacao';

const ABAS: readonly Aba[] = [
  { id: 'categorias', rotulo: 'Categorias' },
  { id: 'produtos', rotulo: 'Produtos' },
  { id: 'saldos', rotulo: 'Saldos' },
  { id: 'lotes-produto', rotulo: 'Lotes de produto' },
  // `lotes` continua sendo a dos lotes da EXPORTAÇÃO: links já mandados com
  // `?aba=lotes` abrem a mesma lista de antes, só com o rótulo mais claro.
  { id: 'lotes', rotulo: 'Lotes do ERP' },
  { id: 'descartados', rotulo: 'Descartados' },
];

const PADRAO = 'categorias';

/** A aba vive na URL: o link dos descartados pode ser mandado para quem confere no ERP. */
function abaDaUrl(valor: string | null): string {
  return valor !== null && ABAS.some((aba) => aba.id === valor) ? valor : PADRAO;
}

/**
 * Sincronização com o ERP (r1-sync).
 *
 * Em cima, o estado da leitura do ERP: a carga inicial (uma vez por armazém),
 * a sincronização periódica e o resumo do que foi gravado. Embaixo, o que
 * foi gravado, uma aba por assunto, cada linha com a sua situação no ERP e as
 * duas datas — quando mudou aqui e quando o ERP foi lido por último.
 *
 * Carga e periódica só o Administrador lê e configura (como na API); os
 * outros perfis acompanham pelo resumo e pelas abas.
 */
export function Sincronizacao() {
  const pode = usePode();
  const administra = pode('sincronizacao.gerenciar');
  const [parametros, definirParametros] = useSearchParams();
  const aba = abaDaUrl(parametros.get('aba'));
  const resumo = useConsulta(obterResumoDaSincronizacao);

  return (
    <Pagina
      contexto="Integração"
      titulo="Sincronização com o ERP"
      faixa={
        <p className="sincronizacao__explicacao">
          O que o painel leu do ERP: categorias, produtos, saldos e lotes por filial e depósito.
          Nada aqui escreve no ERP.
        </p>
      }
    >
      <div className="sincronizacao">
        {administra ? (
          <div className="sincronizacao__topo">
            <CartaoCargaInicial podeDisparar aoAvancar={resumo.recarregar} />
            <CartaoSincronizacaoPeriodica podeConfigurar />
          </div>
        ) : (
          <Cartao titulo="Carga inicial e sincronização periódica">
            <p className="sincronizacao__discreto">
              Só o Administrador acompanha a carga inicial e configura a sincronização periódica. O
              resultado das duas está no resumo e nas abas abaixo.
            </p>
          </Cartao>
        )}

        <ResumoDaSincronizacao consulta={resumo} />

        <div className="sincronizacao__dados">
          <Abas
            rotulo="Dados sincronizados"
            grupo={GRUPO}
            abas={ABAS}
            ativa={aba}
            aoTrocar={(id) => {
              definirParametros(id === PADRAO ? {} : { aba: id });
            }}
          />
          <PainelDeAba id={aba} grupo={GRUPO}>
            {aba === 'categorias' && <AbaCategorias />}
            {aba === 'produtos' && (
              <AbaProdutos podeImprimirEtiqueta={pode('etiquetas.imprimir')} />
            )}
            {aba === 'saldos' && <AbaSaldos />}
            {aba === 'lotes-produto' && (
              <AbaLotesDeProduto podeImprimirEtiqueta={pode('etiquetas.imprimir')} />
            )}
            {aba === 'lotes' && <AbaLotes />}
            {aba === 'descartados' && <AbaDescartados />}
          </PainelDeAba>
        </div>
      </div>
    </Pagina>
  );
}
